import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Volume2, VolumeX, Megaphone, MegaphoneOff } from "lucide-react";
import { useSoundStore } from "@/stores/useSoundStore";

export interface GameHeaderProps {
  onBack?: () => void;
  /** Optional center content — will be absolutely centered (e.g. timer, progress). Use for Match timer. */
  centerContent?: React.ReactNode;
  /** Optional wrapper class for the header row */
  className?: string;
  /** Use compact padding (py-2 px-4) for tighter vertical layout, e.g. Space Defender */
  compact?: boolean;
}

/**
 * Unified game header used by Cat-Mouse, Match, and other mini-games.
 * 2-row layout: Top (Back + Volume) | Bottom (Timer) — prevents overlap on mobile.
 */
export default function GameHeader({ onBack, centerContent, className = "", compact = false }: GameHeaderProps) {
  const { t } = useTranslation();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    toggleSound,
    toggleVoice,
    setGameVolume,
    setVoiceVolume,
  } = useSoundStore();

  const padClass = compact ? "py-2 px-4" : "p-4";
  const marginClass = compact ? "mb-2" : "mb-4";
  const centerPadClass = compact ? "pb-2" : "pb-4";

  return (
    <div className={`w-full flex flex-col max-w-4xl mx-auto ${marginClass} bg-transparent shrink-0 ${className}`} role="banner">
      {/* Top row: Back and Settings — items-center for vertical alignment, extra pt for breathing room */}
      <div className={`w-full flex justify-between items-center gap-4 ${padClass} bg-transparent ${!compact ? "pt-6" : ""}`}>
        {/* LEFT: Back button only */}
        <div className="flex-1 flex justify-start items-center min-w-0">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-gray-50 dark:hover:bg-[hsl(var(--bg3))] text-gray-700 dark:text-gray-300 font-medium transition-colors mt-7"
              aria-label={t("studyMenu.backToDeck")}
            >
              <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
              {t("studyMenu.backToDeck", { defaultValue: "Back to deck" })}
            </button>
          ) : (
            <div />
          )}
        </div>

        {/* RIGHT: Volume sliders — mt-7 to match Back button for same baseline */}
        <div className="flex-1 flex justify-end items-center gap-4 min-w-0 bg-transparent mt-7">
          <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={gameVolume}
              onChange={(e) => setGameVolume(parseFloat(e.target.value))}
              className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
              aria-label={t("common.gameVolume")}
            />
            <button
              type="button"
              onClick={toggleSound}
              className="p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center"
              aria-label={isSoundEnabled ? t("defender.muteSound") : t("defender.unmuteSound")}
            >
              {isSoundEnabled ? (
                <Volume2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={voiceVolume}
              onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
              className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
              aria-label={t("common.voiceVolume")}
            />
            <button
              type="button"
              onClick={toggleVoice}
              className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-gray-400 dark:text-gray-500"}`}
              aria-label={isVoiceEnabled ? t("common.voiceModeOn") : t("common.voiceModeOff")}
            >
              {isVoiceEnabled ? (
                <Megaphone className="w-5 h-5" />
              ) : (
                <MegaphoneOff className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom row: Timer / progress — its own dedicated space, no collisions */}
      {centerContent && (
        <div className={`w-full flex justify-center ${centerPadClass}`}>
          {centerContent}
        </div>
      )}
    </div>
  );
}
